import {SafeAreaView,useSafeAreaInsets} from "react-native-safe-area-context";
import React, { useState, type ReactNode } from 'react';
import { Platform,Pressable, StyleSheet, View,useWindowDimensions } from 'react-native';
import type { IconName, TabKey } from '../domain/types';
import { useTokens } from '../theme/tokens';
import { Icon } from './Icon';
import { Text, useCopy } from '../copy';
import {useDesktop} from './useDesktop';
import {navigationRows} from '../../../shared/navigation-layout.js';

const tabs: ReadonlyArray<{ key: TabKey; label: string; icon: IconName }> = [
  { key: 'overview', label: 'Overblik', icon: 'overview' },
  { key: 'position', label: 'Position', icon: 'position' },
  { key: 'alarms', label: 'Alarmer', icon: 'notification' },
  { key: 'care', label: 'Omsorg', icon: 'care' },
  { key: 'events', label: 'Hændelser', icon: 'events' },
  { key: 'activity', label: 'Aktivitet', icon: 'activity' },
  { key: 'device', label: 'Enhed', icon: 'device' },
];

export function AppShell({ activeTab, onTabChange, children, banner, toolbar, visibleTabs,onFleet,fleetLabel }: { activeTab: TabKey; onTabChange: (tab: TabKey) => void; children: ReactNode; banner?: string; toolbar?: ReactNode; visibleTabs?:TabKey[];onFleet?:()=>void;fleetLabel?:string }) {
  const copy = useCopy();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const {width:viewport,fontScale}=useWindowDimensions();
  const styles = makeStyles(tokens,useSafeAreaInsets());
  const shown=tabs.filter(tab=>!visibleTabs||visibleTabs.includes(tab.key));
  const measurementKey=JSON.stringify([shown.map(tab=>copy(tab.label)),fontScale]);
  const [navigationWidth,setNavigationWidth]=useState(0);
  const [measured,setMeasured]=useState<{key:string;values:Record<string,number>}>({key:'',values:{}});
  const labelWidths=shown.map(tab=>measured.key===measurementKey?measured.values[tab.key]??copy(tab.label).length*8*fontScale:copy(tab.label).length*8*fontScale);
  const rows=navigationRows((navigationWidth||viewport-12)-6,labelWidths);
  const {desktop}=useDesktop();
  if(desktop)return <SafeAreaView edges={['top','bottom']} style={styles.safe}>
    <View style={{borderBottomWidth:1,borderColor:colors.border,paddingHorizontal:24,paddingVertical:8}}>{toolbar}</View>
    <View style={{flex:1,minHeight:0,flexDirection:'row'}}>
      <View accessibilityRole="tablist" testID="desktop-navigation" style={{width:216,padding:16,gap:8,borderRightWidth:1,borderColor:colors.border,backgroundColor:colors.surface}}>
        <Text style={{color:colors.textMuted,fontSize:13,padding:12}}>Qtracker · Web</Text>
        {tabs.filter(tab=>!visibleTabs||visibleTabs.includes(tab.key)).map(tab=>{
          const selected=tab.key===activeTab;
          return <Pressable key={tab.key} testID={`tab-${tab.key}`} accessibilityRole="tab" accessibilityLabel={copy(tab.label)} aria-selected={selected} accessibilityState={{selected}} onPress={()=>onTabChange(tab.key)} style={({pressed})=>({flexDirection:'row',alignItems:'center',gap:12,padding:14,minHeight:50,borderRadius:12,backgroundColor:selected?colors.primary:'transparent',opacity:pressed?.7:1})}>
            <Icon name={tab.icon} size={22} color={selected?colors.primaryInk:colors.textMuted}/><Text style={{fontSize:16,fontWeight:'700',color:selected?colors.primaryInk:colors.text}}>{tab.label}</Text>
          </Pressable>;
        })}
        {onFleet&&<Pressable accessibilityRole="button" onPress={onFleet} style={{padding:14,minHeight:50,borderRadius:12,borderWidth:1,borderColor:colors.border}}><Text style={{color:colors.text,fontSize:16,fontWeight:'700'}}>{fleetLabel}</Text></Pressable>}
      </View>
      <View style={{flex:1,minWidth:0,minHeight:0}}>
        {banner&&<View accessibilityRole="alert" style={styles.banner}><Text style={styles.bannerText}>{banner}</Text></View>}
        <View style={{flex:1,minHeight:0,width:'100%'}}>{children}</View>
      </View>
    </View>
  </SafeAreaView>;
  return (
    <SafeAreaView edges={["top","bottom","left","right"]} style={styles.safe}>{toolbar}
      {banner ? <View accessibilityRole="alert" style={styles.banner}><Icon name="warning" size={20} color={colors.primaryInk} /><Text style={styles.bannerText}>{banner}</Text></View> : null}
      <View style={styles.content}>{children}</View>
      <View accessibilityRole="tablist" testID="bottom-navigation" onLayout={e=>setNavigationWidth(e.nativeEvent.layout.width)} style={styles.navigation}>
        <View pointerEvents="none" accessibilityElementsHidden importantForAccessibility="no-hide-descendants" aria-hidden style={{position:'absolute',width:10000,left:-10000,opacity:0,flexDirection:'row'}}>
          {shown.map(tab=><Text key={tab.key} numberOfLines={1} onLayout={e=>{const value=e.nativeEvent.layout.width;setMeasured(old=>old.key===measurementKey&&old.values[tab.key]===value?old:{key:measurementKey,values:{...(old.key===measurementKey?old.values:{}),[tab.key]:value}});}} style={[styles.tabLabel,{flexShrink:0}]}>{tab.label}</Text>)}
        </View>
        {rows.map((row,index)=><View key={index} style={{flexDirection:'row',width:'100%'}}>{row.map(item => {
          const tab=shown[item.index];
          const selected = tab.key === activeTab;
          return (
            <Pressable
              key={tab.key}
              testID={`tab-${tab.key}`}
              accessibilityRole="tab"
              accessibilityLabel={copy(tab.label)}
              aria-selected={selected} accessibilityState={{ selected }}
              onPress={() => onTabChange(tab.key)}
              style={({ pressed }) => [styles.tab,{width:item.width}, selected && styles.tabSelected, pressed && styles.pressed]}
            >
              <Icon name={tab.icon} size={design.accessible ? 26 : 23} color={selected ? colors.primaryInk : colors.textMuted} />
              <Text numberOfLines={2} style={[styles.tabLabel, selected && styles.tabLabelSelected]}>{tab.label}</Text>
            </Pressable>
          );
        })}</View>)}
      </View>
    </SafeAreaView>
  );
}

const makeStyles = ({colors,design,radius,shadows,spacing,touch,type}: ReturnType<typeof useTokens>, insets={bottom:0},small=false) => StyleSheet.create({
  safe: { flex: 1, backgroundColor: colors.background },
  content: { flex: 1, minHeight: 0 },
  banner: { marginHorizontal: spacing.md, marginTop: spacing.xs, minHeight: touch.min, borderRadius: radius.button, padding: spacing.sm, flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.primary },
  bannerText: { color: colors.primaryInk, flex: 1, fontSize: type.body, fontWeight: '800' },
  navigation: {
    // Native navigation takes up layout space so every scroll host ends above it.
    // SafeAreaView already reserves the system gesture/navigation inset.
    marginHorizontal:6,marginTop:8,marginBottom:8,padding:2,
    borderRadius: radius.lg, borderWidth: 1, borderColor: colors.border,
    backgroundColor: colors.surfaceRaised, gap:4,
    shadowColor: '#000', shadowOpacity: 0.18, shadowOffset: {width:0,height:4},
    shadowRadius: 12, elevation: 8,
  },
  // Identical sizing properties prevent widths from a previous layout surviving a switch.
  tab: { flexGrow:0,flexShrink:0,paddingHorizontal:2,minWidth:0,
    minHeight:60,borderRadius:radius.md,
    alignItems: 'center', justifyContent: 'center', gap: 2 },
  tabSelected: { backgroundColor: colors.primary },
  tabLabel: { color: colors.textMuted, fontSize:13,textAlign:'center', fontWeight: '800' },
  tabLabelSelected: { color: colors.primaryInk },
  pressed: { opacity: 0.72 },
});

export const useStyles = () => makeStyles(useTokens());
